import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, Plus, AlertCircle, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ProcedureCard from './_partials/ProcedureCard';
import { ProceduresGridSkeleton } from './_partials/ProcedureSkeletons';
import SheetProcedureFilter from './_partials/sheets/SheetProcedureFilter';
import { PageHeader } from '@/pages/_partials/mobile-headers';
import { useProcedureTemplates } from '@/lib/hooks/use-procedure-templates';
import { useAdminProcedureTemplates } from '@/lib/hooks/use-admin-procedure-templates';
import { useAuth } from '@/lib/contexts/auth-context';
import { usePermissions } from '@/lib/hooks/use-permissions';
// import { useFavoriteProcedures } from '@/lib/hooks/use-favorite-procedures'; // TODO: réactiver quand prêt sur mobile
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useTranslation } from 'react-i18next';

/**
 * Génère une abréviation à partir du nom de la procédure
 * @param {string} name
 * @returns {string}
 */
function generateAbbreviation(name) {
    if (!name) return '??';
    const words = name.split(' ').filter((w) => w.length > 2);
    if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).charAt(0).toUpperCase() + name.substring(1, 2).toLowerCase();
}

/**
 * Transforme un template API en format attendu par ProcedureCard
 * @param {object} template
 * @returns {object}
 */
function transformTemplate(template) {
    return {
        id: template.uniq_id,
        uniq_id: template.uniq_id,
        name: template.name,
        color: template.color || 'rgb(74, 144, 226)',
        abbreviation: generateAbbreviation(template.name),
        description: template.description || '',
        created_at: template.created_at || null,
        created_by_user_uniq_id: template.created_by_user_uniq_id || null,
        creator: template.creator || null,
        creator_company_uniq_id: template.creator?.company_uniq_id || null,
    };
}

export default function Procedures() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
    const [filters, setFilters] = useState(null);
    const [activeTab, setActiveTab] = useState('my-procedures');
    const { user } = useAuth();
    const { can } = usePermissions();
    // const { isFavorite, toggleFavorite } = useFavoriteProcedures(); // TODO: réactiver quand prêt sur mobile

    // Chargement des sous-comptes
    const companyId = user?.company_uniq_id ?? user?.company?.uniq_id ?? null;
    const [subsidiaries, setSubsidiaries] = useState([]);
    useEffect(() => {
        if (!companyId) return;
        let mounted = true;
        horodatyApi.subsidiaries
            .list(companyId)
            .then((res) => {
                const subs = res?.data?.data ?? res?.data ?? [];
                if (mounted) setSubsidiaries(Array.isArray(subs) ? subs : []);
            })
            .catch(() => {});
        return () => {
            mounted = false;
        };
    }, [companyId]);

    // Récupération des templates depuis l'API
    const { templates, isLoading, error, refetch } = useProcedureTemplates();

    // ── Mock admin templates (mettre à false pour utiliser l'API) ──
    const USE_MOCK_ADMIN = false;

    const mockAdminTemplates = [
        {
            uniq_id: 'mock-admin-1',
            name: t('procedures:constatPhotographiqueDeDommage'),
            description:
                t('procedures:procedureCompletePourConstaterEtDocumenterDesDom'),
            color: 'rgb(74, 144, 226)',
        },
        {
            uniq_id: 'mock-admin-2',
            name: t('procedures:etatDesLieuxLocatif'),
            description: t('procedures:formulaireStandardisePourLesEtatsDesLieuxD'),
            color: 'rgb(52, 220, 167)',
        },
        {
            uniq_id: 'mock-admin-3',
            name: t('procedures:controleDeConformiteChantier'),
            description: t('procedures:suiviDesControlesDeConformiteSurChantierAvec'),
            color: 'rgb(255, 107, 107)',
        },
    ];

    // Récupération des procédures-types (admin) de la super company
    const {
        templates: apiAdminTemplates,
        companyName: apiAdminCompanyName,
        companyTag: apiAdminCompanyTag,
        isLoading: apiAdminLoading,
        error: apiAdminError,
    } = useAdminProcedureTemplates();

    const adminTemplates = USE_MOCK_ADMIN ? mockAdminTemplates : apiAdminTemplates;
    const adminCompanyName = USE_MOCK_ADMIN ? t('procedures:objectif54') : apiAdminCompanyName;
    const adminCompanyTag = USE_MOCK_ADMIN ? 'O54' : apiAdminCompanyTag;
    const adminLoading = USE_MOCK_ADMIN ? false : apiAdminLoading;
    const adminError = USE_MOCK_ADMIN ? null : apiAdminError;

    // Transformer les templates pour l'affichage
    const procedures = templates.map(transformTemplate);
    const adminProcedures = adminTemplates.map(transformTemplate);

    // Extraire les collaborateurs uniques depuis les templates (mes procédures + admin)
    const collaboratorOptions = useMemo(() => {
        const allTemplates = [...procedures, ...adminProcedures];
        const seen = new Set();
        const options = [];
        for (const p of allTemplates) {
            const uid = p.created_by_user_uniq_id;
            if (!uid || seen.has(uid)) continue;
            seen.add(uid);
            options.push({ id: uid, name: p.creator?.user || uid });
        }
        return options;
    }, [procedures, adminProcedures]);

    // Appliquer les filtres sur une liste de procédures
    // isAdmin = true pour les procédures-types (seuls recherche + période s'appliquent)
    const applyFilters = (list, { isAdmin = false } = {}) => {
        let result = list;

        // Filtre recherche
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            result = result.filter((p) => p.name.toLowerCase().includes(q));
        }

        // Filtre période
        if (filters?.period && filters.period !== 'all') {
            const days = parseInt(filters.period, 10);
            if (!isNaN(days)) {
                const cutoff = new Date();
                cutoff.setDate(cutoff.getDate() - days);
                result = result.filter((p) => p.created_at && new Date(p.created_at) >= cutoff);
            }
        }

        // Les filtres collaborateur et sous-compte ne s'appliquent pas aux procédures-types
        if (isAdmin) return result;

        // Filtre collaborateurs
        if (filters?.collaborator && filters.collaborator !== 'all' && Array.isArray(filters.collaborator)) {
            const ids = new Set(filters.collaborator);
            result = result.filter((p) => p.created_by_user_uniq_id && ids.has(p.created_by_user_uniq_id));
        }

        // Filtre sous-comptes
        if (filters?.subsidiaries && Array.isArray(filters.subsidiaries)) {
            const entityIds = new Set(filters.subsidiaries);
            result = result.filter((p) => {
                if (!p.creator_company_uniq_id) return false;
                if (entityIds.has('own') && p.creator_company_uniq_id === companyId) return true;
                return entityIds.has(p.creator_company_uniq_id);
            });
        }

        return result;
    };

    const filteredProcedures = applyFilters(procedures);
    const filteredAdminProcedures = applyFilters(adminProcedures, { isAdmin: true });

    // Gestion de la soumission des filtres
    const handleFilterSubmit = (filterData) => {
        setFilters(filterData);
        setIsFilterPanelOpen(false);
    };

    const hasActiveFilters =
        filters &&
        (filters.period !== 'all' ||
            (filters.collaborator !== 'all' && Array.isArray(filters.collaborator)) ||
            Array.isArray(filters.subsidiaries));

    const hasAdminProcedures = adminProcedures.length > 0 || adminLoading;

    return (
        <>
            {/* Page Header */}
            <PageHeader
                title={t('procedures:procedures')}
                actions={
                    activeTab === 'my-procedures' ? (
                        <Button variant="link" color="secondary" onClick={() => navigate('/procedures/archives')}>
                            {t('procedures:archives')}
                        </Button>
                    ) : (
                        <span className="h-[37px]"></span>
                    )
                }
            />

            {/* Actions Bar */}
            <div className="flex items-center justify-between gap-3 px-4 sm:px-[20px] py-3 min-h-[62px]">
                {/* Search input */}
                <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                        type="text"
                        placeholder={t('procedures:rechercherUneProcedure')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 bg-white border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                </div>
                {activeTab === 'my-procedures' && (
                    <div className="flex gap-3">
                        {can('CREATE_PROCEDURES') && (
                            <Button
                                variant="solid"
                                color="primary"
                                onClick={() => navigate('/procedures/create')}
                                className="flex items-center gap-2 shrink-0"
                            >
                                <Plus className="w-4 h-4" />
                                <span className="hidden md:inline">{t('procedures:creerUneProcedure')}</span>
                            </Button>
                        )}

                        <Button
                            variant="outline"
                            color={hasActiveFilters ? 'success' : 'neutral'}
                            onClick={() => setIsFilterPanelOpen(true)}
                            className="flex items-center gap-2 shrink-0"
                        >
                            <Filter className="w-4 h-4" />
                            <span className="hidden md:inline">{t('procedures:filtrer')}</span>
                        </Button>
                    </div>
                )}
            </div>

            {/* Tab Bar */}
            {hasAdminProcedures && (
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <div className="bg-card border-y border-border px-[20px]">
                        <TabsList className="inline-flex h-auto items-center gap-[32px] rounded-none bg-transparent p-0">
                            <TabsTrigger
                                value="my-procedures"
                                className="relative rounded-none bg-transparent px-0 py-[14px] text-[15px] font-normal shadow-none transition-colors data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground"
                            >
                                {t('procedures:mesProcedures')}
                                <div
                                    className="absolute bottom-0 left-0 right-0 h-[3px] rounded-t-[2px] bg-secondary transition-opacity data-[state=active]:opacity-100"
                                    data-state={activeTab === 'my-procedures' ? 'active' : 'inactive'}
                                    style={{ opacity: activeTab === 'my-procedures' ? 1 : 0 }}
                                />
                            </TabsTrigger>
                            <TabsTrigger
                                value="admin-procedures"
                                className="relative rounded-none bg-transparent px-0 py-[14px] text-[15px] font-normal shadow-none transition-colors data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground"
                            >
                                {adminCompanyName || t('procedures:proceduresTypes')}
                                <div
                                    className="absolute bottom-0 left-0 right-0 h-[3px] rounded-t-[2px] bg-secondary transition-opacity"
                                    style={{ opacity: activeTab === 'admin-procedures' ? 1 : 0 }}
                                />
                            </TabsTrigger>
                        </TabsList>
                    </div>

                    <TabsContent value="my-procedures" className="mt-0">
                        <div className="p-4 pb-[120px] md:pb-4">
                            <ProceduresGridContent
                                procedures={filteredProcedures}
                                isLoading={isLoading}
                                error={error}
                                refetch={refetch}
                                navigate={navigate}
                            />
                        </div>
                    </TabsContent>

                    <TabsContent value="admin-procedures" className="mt-0">
                        <div className="p-4 pb-[120px] md:pb-4">
                            <ProceduresGridContent
                                procedures={filteredAdminProcedures}
                                isLoading={adminLoading}
                                error={adminError}
                                refetch={() => {}}
                                navigate={navigate}
                                isAdmin
                                companyTag={adminCompanyTag}
                            />
                        </div>
                    </TabsContent>
                </Tabs>
            )}

            {/* Sans onglets (pas de procédures admin disponibles) */}
            {!hasAdminProcedures && (
                <div className="p-4 pb-[120px] md:pb-4">
                    <ProceduresGridContent
                        procedures={filteredProcedures}
                        isLoading={isLoading}
                        error={error}
                        refetch={refetch}
                        navigate={navigate}
                    />
                </div>
            )}

            {/* Sheet Filtrer et trier */}
            <SheetProcedureFilter
                open={isFilterPanelOpen}
                onClose={() => setIsFilterPanelOpen(false)}
                onSubmit={handleFilterSubmit}
                filters={filters}
                collaborators={collaboratorOptions}
                subsidiaries={subsidiaries}
            />
        </>
    );
}

function ProceduresGridContent({
    procedures,
    isLoading,
    error,
    refetch,
    navigate,
    isAdmin = false,
    companyTag,
    // isFavorite, // TODO: réactiver quand prêt sur mobile
    // onToggleFavorite, // TODO: réactiver quand prêt sur mobile
}) {
    const { t } = useTranslation();
    return (
        <>
            {/* État de chargement */}
            {isLoading && <ProceduresGridSkeleton count={8} />}

            {/* État d'erreur */}
            {error && !isLoading && (
                <div className="text-center py-12">
                    <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">{t('procedures:erreurLorsDuChargementDesProcedures')}</p>
                    {import.meta.env.DEV && (
                        <pre className="text-left bg-destructive/10 text-destructive p-4 rounded-lg mb-4 text-xs overflow-auto max-w-2xl mx-auto">
                            {error?.response?.data?.message || error?.message || JSON.stringify(error, null, 2)}
                        </pre>
                    )}
                    <Button variant="outline" color="neutral" onClick={() => refetch(true)}>
                        {t('procedures:reessayer')}
                    </Button>
                </div>
            )}

            {/* Empty state */}
            {!isLoading && !error && procedures.length === 0 && (
                <div className="text-center py-12">
                    <p className="text-muted-foreground">{t('procedures:aucuneProcedureTrouvee')}</p>
                </div>
            )}

            {/* Procedures Grid */}
            {!isLoading && !error && procedures.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-[15px]">
                    {procedures.map((procedure) => (
                        <ProcedureCard
                            key={procedure.uniq_id}
                            procedure={procedure}
                            onClick={() => navigate(`/procedures/${procedure.uniq_id}`)}
                            isAdmin={isAdmin}
                            companyTag={companyTag}
                        />
                    ))}
                </div>
            )}
        </>
    );
}
